import React from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HERO_IMG =
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80';

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="hero"
      className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden bg-gradient-hero"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse 80% 50% at 20% 40%, rgba(212,160,36,0.25) 0%, transparent 55%),
                            radial-gradient(circle at 85% 15%, rgba(94,234,212,0.12) 0%, transparent 45%)`,
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,#060a12_95%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div className="space-y-8 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-full px-4 py-2">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              <span className="text-primary-light text-sm font-semibold tracking-wide">{t.hero.badge}</span>
            </div>

            <div className="space-y-2">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white font-heading leading-[1.05] tracking-tight">
                {t.hero.titleA}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light via-primary to-accent">
                  {t.hero.titleB}
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-slate-400 font-heading font-semibold max-w-xl leading-snug">
                {t.hero.titleC}
              </p>
            </div>

            <p className="text-lg text-slate-400 leading-relaxed max-w-xl border-l-2 border-primary/40 pl-5">
              {t.hero.lead}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/s"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-dark px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-glow hover:-translate-y-0.5"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#oppdag"
                className="inline-flex items-center justify-center gap-2 border border-border-dark hover:border-accent/50 text-slate-200 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all bg-card-dark/40"
              >
                {t.hero.ctaSecondary}
              </a>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">{t.hero.legal}</p>
          </div>

          <div className="relative order-1 lg:order-2">
            <div className="relative aspect-[4/5] max-h-[520px] mx-auto max-w-md lg:max-w-none rounded-[2rem] overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/50">
              <img
                src={HERO_IMG}
                alt={t.advantages.imageAlt}
                className="w-full h-full object-cover scale-105"
                width={600}
                height={750}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/90 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div className="bg-card-dark/95 backdrop-blur border border-border-dark rounded-2xl p-4 shadow-xl max-w-[240px]">
                  <p className="text-white font-bold text-sm">{t.hero.statTitle}</p>
                  <p className="text-slate-400 text-xs mt-1">{t.hero.statSub}</p>
                </div>
                <div className="bg-gradient-to-r from-primary to-primary-dark text-dark font-bold text-sm px-4 py-3 rounded-xl shadow-lg self-end">
                  {t.hero.priceChip}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <a
        href="#oppdag"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 hover:text-accent transition-colors animate-bounce"
        aria-label={t.nav.discover}
      >
        <ChevronDown className="w-8 h-8" />
      </a>
    </section>
  );
};

export default Hero;
